import {useImmer} from "use-immer";
import {z} from "zod";

type TObjectWithErrors<T extends Record<string, unknown>> = T & {
  errors: {
    [K in keyof T]: string | null;
  } & {overall: string | null};
};

export const useZodState = <T extends z.ZodType, TState extends z.infer<T> & Record<string, unknown>>(
  schema: T,
  initialState: TState,
) => {
  const [state, setState] = useImmer<TObjectWithErrors<TState>>({
    ...initialState,
    errors: Object.keys(initialState).reduce(
      (acc, key) => {
        (acc as Record<string, string | null>)[key] = null;
        return acc;
      },
      {overall: null} as TObjectWithErrors<TState>["errors"],
    ),
  });

  const validate = () => {
    const results = schema.safeParse(state);
    if (!results.success) {
      setState(d => {
        const errors = d.errors as Record<string, string | null>;
        results.error.issues.forEach(issue => {
          const key = issue.path[0];
          if (typeof key === "string" && key in errors) {
            errors[key] = issue.message;
          }
        });
      });
    }
    return results.success;
  };

  const singleKeySetter = (key: keyof TState, value: TState[keyof TState]) => {
    setState(d => {
      (d as Record<string, unknown>)[key as string] = value;
      (d.errors as Record<string, string | null>)[key as string] = null;
    });
  };

  return [state, singleKeySetter, validate, setState] as const;
};
